import { DataType } from '../../data';
import { shallowOmit } from '../../utils/object-utils';
import type { ICTShapeProperties, IShapeDelta } from '@tencent/slide-sdk-pb-model';
import type { DeepReadonly } from 'ts-essentials';
import type { IValidSetShapePropertiesMutationData } from './valid-mutation-data';
import type {
    IConnectorData,
    IDrawingShapeData,
    IGraphicFrameBaseData,
    IGroupShapeData,
    IPictureData,
    IShapeData,
    ShapeDataType,
    ITableData,
} from '../../data';
import type { DeltaOf } from '../../utils/delta';

export class ShapeDeltaUtils {
    static fromMutationData(
        data: DeepReadonly<Omit<IValidSetShapePropertiesMutationData, 'type'>>,
    ): DeepReadonly<IShapeDelta> {
        return {
            ...data.shapeDelta,
        };
    }

    static fromShapeData(data: DeepReadonly<IShapeData>): DeepReadonly<IShapeDelta> {
        // pathString 和 egPath2D 信息重复，数据中不再需要 egPath2D，这里删除 egPath2D
        const spPr = this.getSpPrWithoutEgPath2D(data.spPr);
        return {
            spPr,
            nvPr: data.nvPr && {
                ...shallowOmit(data.nvPr, 'txBox'),
                shape: {
                    txBox: data.nvPr.txBox,
                },
            },
            style: data.style,
            shape: {
                useBgFill: data.useBgFill,
            },
        };
    }

    static getSpPrWithoutEgPath2D(data: DeepReadonly<ICTShapeProperties>) {
        const { custGeom } = data;
        const { pathLst } = custGeom ?? {};
        const { path } = pathLst ?? {};
        return {
            ...shallowOmit(data, 'xfrm'),
            custGeom: custGeom && {
                ...custGeom,
                pathLst: pathLst && {
                    ...pathLst,
                    path: path?.map((item) => ({
                        ...item,
                        egPath2D: item.pathString ? undefined : item.egPath2D,
                    })),
                },
            },
        };
    }

    static fromGroupShapeData(data: DeepReadonly<IGroupShapeData>): DeepReadonly<IShapeDelta> {
        return {
            spPr: shallowOmit(data.spPr, 'xfrm'),
            nvPr: data.nvPr,
        };
    }

    static fromConnectorData(data: DeepReadonly<IConnectorData>): DeepReadonly<IShapeDelta> {
        return {
            spPr: shallowOmit(data.spPr, 'xfrm'),
            nvPr: data.nvPr && {
                ...shallowOmit(data.nvPr, 'stCxn', 'endCxn'),
                connector: {
                    stCxn: data.nvPr.stCxn,
                    endCxn: data.nvPr.endCxn,
                },
            },
            style: data.style,
        };
    }

    static fromPictureData(data: DeepReadonly<IPictureData>): DeepReadonly<IShapeDelta> {
        return {
            spPr: shallowOmit(data.spPr, 'xfrm'),
            nvPr: data.nvPr && {
                ...shallowOmit(data.nvPr, 'preferRelativeResize'),
                picture: {
                    preferRelativeResize: data.nvPr.preferRelativeResize,
                },
            },
            style: data.style,
            picture: {
                blipFill: data.blipFill,
            },
        };
    }

    static fromGraphicObjectFrameData(data: DeepReadonly<IGraphicFrameBaseData>): DeepReadonly<IShapeDelta> {
        return {
            spPr: shallowOmit(data.spPr, 'xfrm'),
            nvPr: data.nvPr,
            graphicalObjectFrame: {
                bwMode: data.bwMode,
            },
        };
    }

    static fromTableData(data: DeepReadonly<IGraphicFrameBaseData>): DeepReadonly<IShapeDelta> {
        return {
            spPr: shallowOmit(data.spPr, 'xfrm'),
            nvPr: data.nvPr,
            graphicalObjectFrame: {
                bwMode: data.bwMode,
            },
            table: data.graphic?.graphicData?.tbl?.tblPr,
        };
    }

    static fromDrawingShapeData(data: DeepReadonly<IDrawingShapeData>): DeepReadonly<IShapeDelta> {
        switch (data.type) {
            case DataType.Shape:
                return ShapeDeltaUtils.fromShapeData(data);
            case DataType.GroupShape:
                return ShapeDeltaUtils.fromGroupShapeData(data);
            case DataType.Connector:
                return ShapeDeltaUtils.fromConnectorData(data);
            case DataType.Picture:
                return ShapeDeltaUtils.fromPictureData(data);
            case DataType.GraphicalObjectFrameTable:
                return ShapeDeltaUtils.fromTableData(data);
            case DataType.GraphicalObjectFrameChart:
                return ShapeDeltaUtils.fromGraphicObjectFrameData(data);
            case DataType.GraphicalObjectFrameSmartArt:
                return ShapeDeltaUtils.fromGraphicObjectFrameData(data);
            default:
                throw new Error(`unsupported shape data: ${data}`);
        }
    }

    static toShapeDataDelta(delta: IShapeDelta): DeltaOf<Omit<IShapeData, 'type'>> {
        return {
            spPr: delta.spPr,
            nvPr: delta.nvPr && {
                ...shallowOmit(delta.nvPr, 'shape'),
                ...delta.nvPr.shape,
            },
            style: delta.style,
            useBgFill: delta.shape?.useBgFill,
        };
    }

    static toGroupShapeDataDelta(delta: IShapeDelta): DeltaOf<Omit<IGroupShapeData, 'type'>> {
        return {
            spPr: delta.spPr,
            nvPr: delta.nvPr,
        };
    }

    static toConnectorDataDelta(delta: IShapeDelta): DeltaOf<Omit<IConnectorData, 'type'>> {
        return {
            spPr: delta.spPr,
            nvPr: delta.nvPr && {
                ...shallowOmit(delta.nvPr, 'connector'),
                ...delta.nvPr.connector,
            },
            style: delta.style,
        };
    }

    static toPictureDataDelta(delta: IShapeDelta): DeltaOf<Omit<IPictureData, 'type'>> {
        return {
            spPr: delta.spPr,
            nvPr: delta.nvPr && {
                ...shallowOmit(delta.nvPr, 'picture'),
                ...delta.nvPr.picture,
            },
            style: delta.style,
            blipFill: delta.picture?.blipFill,
        };
    }

    static toTableDataDelta(delta: IShapeDelta): DeltaOf<Omit<ITableData, 'type'>> {
        return {
            spPr: delta.spPr,
            nvPr: delta.nvPr,
            graphic: {
                graphicData: {
                    tbl: {
                        tblPr: delta.table,
                    },
                },
            },
            bwMode: delta.graphicalObjectFrame?.bwMode,
        };
    }

    static toGraphicObjectFrameDataDelta(delta: IShapeDelta): DeltaOf<Omit<IGraphicFrameBaseData, 'type'>> {
        return {
            spPr: delta.spPr,
            nvPr: delta.nvPr,
            graphic: delta.graphicalObjectFrame?.graphic,
            bwMode: delta.graphicalObjectFrame?.bwMode,
        };
    }

    static toDrawingShapeDataDelta(
        shapeType: ShapeDataType,
        delta: IShapeDelta,
    ):
        | DeltaOf<IShapeData>
        | DeltaOf<IGroupShapeData>
        | DeltaOf<IConnectorData>
        | DeltaOf<IPictureData>
        | DeltaOf<IGraphicFrameBaseData> {
        switch (shapeType) {
            case DataType.Shape:
                return ShapeDeltaUtils.toShapeDataDelta(delta);
            case DataType.GroupShape:
                return ShapeDeltaUtils.toGroupShapeDataDelta(delta);
            case DataType.Connector:
                return ShapeDeltaUtils.toConnectorDataDelta(delta);
            case DataType.Picture:
                return ShapeDeltaUtils.toPictureDataDelta(delta);
            case DataType.GraphicalObjectFrameTable:
                return ShapeDeltaUtils.toTableDataDelta(delta);
            case DataType.GraphicalObjectFrameChart:
            case DataType.GraphicalObjectFrameSmartArt:
            case DataType.GraphicalObjectFrameUnknown:
                return ShapeDeltaUtils.toGraphicObjectFrameDataDelta(delta);
            default:
                throw new Error(`unsupported shape type: ${shapeType}`);
        }
    }
}
